import { DEFAULT_MAPPINGS } from '../constants';
import type { Label } from '../types';

/**
 * Normalizes a token by trimming whitespace and converting to lowercase
 */
export const normalizeToken = (raw: string): string => raw.trim().toLowerCase();

/**
 * Removes leading dot from extension if present
 */
export const stripLeadingDot = (extOrToken: string): string =>
  extOrToken.startsWith('.') ? extOrToken.slice(1) : extOrToken;

/**
 * Checks if token is a wildcard MIME type (e.g., 'image/*')
 */
export const isWildcardMime = (token: string): boolean =>
  token.endsWith('/*') && token.includes('/');

/**
 * Checks if token is a MIME type (e.g., 'application/pdf')
 */
export const isMime = (token: string): boolean =>
  token.includes('/') && !isWildcardMime(token);

/**
 * Handles unknown tokens by inferring type from structure
 */
export const handleUnknownToken = (token: string): string => {
  if (token.includes('/')) {
    const major = token.slice(0, token.indexOf('/'));
    switch (major) {
      case 'image':
        return 'Images';
      case 'audio':
        return 'Audio';
      case 'video':
        return 'Video';
      case 'text':
        return 'Text';
      case 'application':
        return 'Documents';
      default:
        return 'Files';
    }
  }
  return token.toUpperCase();
};

/**
 * Checks whether a file matches an HTML accept attribute string.
 * Handles file extensions (e.g. '.csv'), MIME types (e.g. 'text/csv'),
 * and wildcard MIME types (e.g. 'image/*').
 */
export const fileMatchesAccept = (file: File, accept: string): boolean => {
  if (!accept || typeof accept !== 'string') {
    return true;
  }

  const tokens = accept.split(',').map(normalizeToken).filter(Boolean);

  return tokens.some((token) => {
    if (isWildcardMime(token)) {
      const prefix = token.slice(0, token.indexOf('/*'));
      return file.type.startsWith(`${prefix}/`);
    }
    if (isMime(token)) {
      return file.type === token;
    }
    // Extension token (e.g. '.csv')
    const ext = token.startsWith('.') ? token : `.${token}`;
    return file.name.toLowerCase().endsWith(ext);
  });
};

/**
 * Parses accept string into user-friendly labels
 * @param accept - HTML accept attribute string (e.g., 'image/jpeg,.pdf,image/*')
 * @returns Array of user-friendly labels (e.g., ['JPEG', 'PDF', 'Images'])
 */
export const acceptToLabels = (accept: string): Label[] => {
  if (!accept || typeof accept !== 'string') {
    return [];
  }

  const tokens = accept.split(',').map(normalizeToken).filter(Boolean);
  const out: Label[] = [];
  const seen = new Set<string>();

  for (const token of tokens) {
    let label: Label | undefined;

    if (isWildcardMime(token)) {
      label = DEFAULT_MAPPINGS.wildcards[token] ?? handleUnknownToken(token);
    } else if (isMime(token)) {
      label = DEFAULT_MAPPINGS.mimes[token] ?? handleUnknownToken(token);
    } else {
      const ext = stripLeadingDot(token);
      label = DEFAULT_MAPPINGS.exts[ext] ?? ext.toUpperCase();
    }

    if (label && !seen.has(label)) {
      seen.add(label);
      out.push(label);
    }
  }

  return out;
};
